import { useEffect, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { Card, Stack, TextField, Typography, IconButton, InputAdornment, Box, Button, Tooltip } from '@mui/material';
import { Visibility, VisibilityOff, YouTube, OpenInNew } from '@mui/icons-material';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';

const SAVED_PASSWORD_MASK = '********';

const CustomEmail = () => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const [host, setHost] = useState('');
  const isCustomDomain = CommonHelper.CustomDomain();
  const isPortalAdmin = CommonHelper.GetBrandInfoFromLocalStorage("is_portal_admin");
  const [port, setPort] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [password, setPassword] = useState('');
  const [receiverEmail, setReceiverEmail] = useState('');
  const [hasCustomEmailConfig, setHasCustomEmail] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [testmailLoading, setTestmailLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordChanged, setPasswordChanged] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCustomEmail();
  }, []);

  const fetchCustomEmail = () => {
    HttpServices.Get({
      url: userModuleURL.getCustomEmailDetails,
      successCallback: fetchEmailSuccessCallback,
      failureCallback: fetchEmailFailureCallback,
    });
  };

  const fetchEmailSuccessCallback = (data) => {
    if (data) {
      setHost(data.host || '');
      setPort(data.port || '');
      setNotifyEmail(data.username || '');
      setPassword(data.password || (data.has_custom_email ? SAVED_PASSWORD_MASK : ''));
      setPasswordChanged(false);
      setReceiverEmail(data.receiverEmail || '');
      setHasCustomEmail(data.has_custom_email || false);
    }
  };

  const fetchEmailFailureCallback = () => {
    setSaveLoading(false);
  };

  const deleteConfig = () => {
    setDeleteLoading(true);
    HttpServices.Post({
      url: userModuleURL.deleteCustomEmailDetails,
      body: {},
      successCallback: deleteEmailConfigSuccessCallback,
      failureCallback: deleteEmailConfigFailureCallback,
    });
  };

  const deleteEmailConfigSuccessCallback = () => {
    setDeleteLoading(false);
    fetchCustomEmail();
  };

  const deleteEmailConfigFailureCallback = () => setDeleteLoading(false);

  const validateFields = () => {
    let newErrors = {};

    if (!host) newErrors.host = `SMTP ${translate('manage_branding.custom_email.host_is_required')}`;
    if (!port) {
      newErrors.port = `SMTP ${translate('manage_branding.custom_email.port_is_required')}`;
    } else if (isNaN(port) || port <= 0 || port > 65535) {
      newErrors.port = `${translate('manage_branding.custom_email.port_must_between')} 1 ${translate(
        'common.and'
      )} 65535`;
      //'Port must be between 1 and 65535';
    }
    if (!notifyEmail) newErrors.notifyEmail = translate('manage_branding.custom_email.notify_email_is_required');
    if (!hasCustomEmailConfig && !password) newErrors.password = translate('manage_branding.custom_email.password_is_required');
    if (!receiverEmail) newErrors.receiverEmail = translate('manage_branding.custom_email.test_email_is_required');

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showToast(translate('manage_branding.custom_email.please_fix_errors'), 'e');
      return false;
    }

    return true;
  };

  const getCustomEmailPayload = () => {
    const payload = {
      host,
      port,
      notify_email: notifyEmail,
      sender_email: receiverEmail,
    };

    if (isPasswordChanged && password) {
      payload.notify_email_password = password;
    }

    return payload;
  };

  const sendSampleEmail = () => {
    if (!validateFields()) return;

    setTestmailLoading(true);
    HttpServices.Post({
      url: userModuleURL.sendSampleCustomEmail,
      body: getCustomEmailPayload(),
      successCallback: sendSampleEmailSuccessCallback,
      failureCallback: sendSampleEmailFailureCallback,
    });
  };

  const sendSampleEmailSuccessCallback = (_, message) => {
    showToast(message, 's');
    setTestmailLoading(false);
  };

  const sendSampleEmailFailureCallback = (message) => {
    showToast(message, 'e');
    setTestmailLoading(false);
  };

  const saveEmail = () => {
    if (!validateFields()) return;

    setSaveLoading(true);
    HttpServices.Post({
      url: userModuleURL.saveCustomEmailDetails,
      body: getCustomEmailPayload(),
      successCallback: emailSuccessCallback,
      failureCallback: emailFailureCallback,
    });
  };

  const emailSuccessCallback = (_, message) => {
    showToast(message, 's');
    setSaveLoading(false);
    fetchCustomEmail();
  };

  const emailFailureCallback = (message) => {
    showToast(message, 'e');
    setSaveLoading(false);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPasswordChanged(true);
  };

  const handleSMTPerTest = () => {
    window.open(`https://www.smtper.net/`, '_blank');
  };

  return (
    <>
      <Card
        sx={{
          height: 'calc(100vh - 160px)',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <Stack direction="row" alignItems="left" justifyContent="" sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {translate('manage_branding.custom_email.custom_email', 'Custom Email')}
          </Typography>
          {(!isCustomDomain || isPortalAdmin) && <Button
            sx={{ ml: 2 }}
            variant="outlined"
            startIcon={<YouTube />}
            onClick={() => window.open('https://www.youtube.com/watch?v=DiaVz00L3fc&ab_channel=Brand2Social', '_blank')}
            color="primary"
          >
            {translate('manage_branding.custom_email.tutorial', 'Tutorial')}
          </Button>}
        </Stack>
        <Box sx={{ p: 4, width: 700, mx: 'auto', mt: 5, boxShadow: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            {translate('manage_branding.custom_email.custom_email_configuration')}
          </Typography>

          <Stack spacing={3}>
            <TextField
              label={`SMTP ${translate('manage_branding.custom_email.host')}`}
              value={host}
              onChange={(e) => setHost(e.target.value)}
              fullWidth
              error={!!errors.host}
              helperText={errors.host}
            />
            <TextField
              label={`SMTP ${translate('manage_branding.custom_email.port')}`}
              value={port}
              onChange={(e) => setPort(e.target.value)}
              fullWidth
              type="number"
              error={!!errors.port}
              helperText={errors.port}
            />
            <TextField
              label={translate('manage_branding.custom_email.notification_email')}
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              fullWidth
              error={!!errors.notifyEmail}
              helperText={errors.notifyEmail}
            />
            <TextField
              label={translate('manage_branding.custom_email.notification_email_app_password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              placeholder="********"
              autoComplete="off"
              onChange={handlePasswordChange}
              fullWidth
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              width="100%"
              p={2}
              border="1px solid #ddd"
              borderRadius={2}
              boxShadow={1}
              bgcolor="white"
            >
              <TextField
                label={translate('manage_branding.custom_email.test_email')}
                value={receiverEmail}
                onChange={(e) => setReceiverEmail(e.target.value)}
                sx={{ flexGrow: 1 }}
                error={!!errors.receiverEmail}
                helperText={errors.receiverEmail}
              />
              <StyledComponent.B2SLoadingButton
                variant="outlined"
                loading={testmailLoading}
                onClick={sendSampleEmail}
                color="primary"
                sx={{ height: '56px', minWidth: '140px', marginTop: '0px' }}
              >
                {translate('manage_branding.custom_email.send_test_email')}
              </StyledComponent.B2SLoadingButton>
              <Tooltip title={translate('manage_branding.custom_email.test_with_smtper_tooltip')}>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  onClick={handleSMTPerTest}
                  sx={{ height: '56px', minWidth: '140px', marginTop: '0px' }}
                >
                  {translate('manage_branding.custom_email.test_with_smtper')}
                </Button>
              </Tooltip>
            </Box>

            <StyledComponent.B2SLoadingButton
              variant="outlined"
              loading={saveLoading}
              onClick={saveEmail}
              color="primary"
            >
              {hasCustomEmailConfig
                ? translate('manage_branding.custom_email.update_config')
                : translate('manage_branding.custom_email.save_config')}
            </StyledComponent.B2SLoadingButton>
            {hasCustomEmailConfig && (
              <>
                <StyledComponent.B2SLoadingButton
                  variant="outlined"
                  loading={deleteLoading}
                  onClick={deleteConfig}
                  color="error"
                >
                  {translate('manage_branding.custom_email.delete_config')}
                </StyledComponent.B2SLoadingButton>
              </>
            )}
          </Stack>
        </Box>
      </Card>
    </>
  );
};

export default CustomEmail;
